import React, { useRef } from "react";
import * as Yup from "yup";
import Image from "next/image";
import dynamic from "next/dynamic";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { ReCAPTCHA } from "@/lib/vendor";
import { Formik, Form, Field, ErrorMessage } from "formik";

import apiService from "@/common/services/apiService";
const Button = dynamic(() => import("@/Components/Button"));
import { hideFormLoader, showFormLoader } from "@/store/loaderSlice";
import { downloadFile } from "@/utils/common";

interface QuoteFormValues {
  email: string;
  fullname: string;
  company_name: string;
  job_title: string;
}
const DownloadWhitepaper: React.FC<{ data: any }> = ({ data }) => {
  const dispatch = useDispatch();
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const sitekey = process.env.NEXT_PUBLIC_RECAPTCHA_V3;

  const initialValues: QuoteFormValues = {
    email: "",
    fullname: "",
    job_title: "",
    company_name: "",
  };

  const validationSchema = Yup.object({
    fullname: Yup.string().trim().required("Full name is required."),
    company_name: Yup.string()
      .trim()
      .optional()
      .min(2, "Company name is too short")
      .max(100, "Company name is too long"),
    job_title: Yup.string()
      .trim()
      .min(2, "Job title is too short")
      .max(100, "Job title is too long")
      .optional(),
    email: Yup.string()
      .trim()
      .email("Email is not valid.")
      .max(100, "Email is too long")
      .required("Email is required.")
      .matches(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/, "Invalid email format"),
  });

  const validateCaptchaV3 = () => {
    return new Promise((resolve) => {
      reCaptchaRef?.current?.executeAsync()?.then(async (value: unknown) => {
        resolve(value);
        // const captchaRes = await axios.post(`/api/validateCaptchaV3`, {token: value});
        // if (captchaRes.status === 200 && captchaRes.data.success) resolve(true); else reject(false);
      });
    });
  };

  const handleSubmit = async (
    values: QuoteFormValues,
    { resetForm, setSubmitting }: any
  ) => {
    try {
      const isHuman = await validateCaptchaV3();
      if (!isHuman) return false;
      dispatch(showFormLoader());
      const payload = {
        ...values,
        page_link: window.location.href,
        captcha_token_v3: isHuman as string,
      } as Record<string, any>;
      await apiService
        .authPost("download-leads/create", payload)
        .then((res: any) => {
          dispatch(hideFormLoader());
          resetForm();
          setSubmitting(false);
          if (!res.data.success)
            toast.error(res?.data?.message ?? "Something went wrong");
          else if (res.status === 200) {
            reCaptchaRef?.current?.reset();
            toast.success(res?.data?.message ?? "Your request has been sent.");
            const attributes =
              data?.data[0].attributes.blocks[0].whitepaper.data.attributes;
            if (attributes?.url) downloadFile(attributes.url, attributes.name);
          } else toast.error(res?.data?.message ?? "Something went wrong");
          return res;
        });
    } catch (error) {
      console.log("handleSubmit ~ error:", error);
    }
  };

  return (
    <div className="sm:bg-[url(/images/Quote-bg.webp)] bg-[url(/images/bg-mobile-quote.png)] bg-no-repeat bg-cover bg-center">
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({ handleChange, values }) => (
          <Form
            className="xl:pt-12 xl:px-12 xl:pb-6 md:px-8 md:pt-8 md:pb-[50px] px-4 pt-6 pb-6 h-full items-stretch"
            aria-label="Request for Proposal Form"
          >
            <h5 className="text-heading-5 font-bold text-primary-text">
              Download Whitepaper
            </h5>

            <div className="grid lg:grid-cols-1 gap-4 md:mt-6 mt-4 h-full items-stretch">
              <div className="grid lg:grid-cols-2 gap-4 h-full items-stretch">
                <div className="input-group relative w-full">
                  <Field
                    type="text"
                    name="fullname"
                    value={values.fullname}
                    placeholder=""
                    aria-label={"Full name"}
                    aria-describedby={`fullname-error`}
                    aria-required={true}
                    onChange={handleChange}
                    className="input text-subtitle-3 font-bold mb-[10px]"
                  />
                  <label
                    htmlFor="fullname"
                    className="text-subtitle-5 font-regular"
                  >
                    Full name*
                  </label>
                  <ErrorMessage
                    name="fullname"
                    component="span"
                    className="text-red-500"
                  />
                </div>
                <div className="input-group relative w-full">
                  <Field
                    type="email"
                    name="email"
                    placeholder=""
                    value={values.email}
                    aria-label={"email"}
                    aria-required={true}
                    onChange={handleChange}
                    aria-describedby={`email-error`}
                    className="input text-subtitle-3 font-bold mb-[10px]"
                  />
                  <label
                    htmlFor="email"
                    className="text-subtitle-5 font-regular"
                  >
                    email*
                  </label>
                  <ErrorMessage
                    name="email"
                    component="span"
                    className="text-red-500"
                  />
                </div>
                <div className="input-group relative w-full">
                  <Field
                    type="text"
                    name="company_name"
                    value={values.company_name}
                    placeholder=""
                    aria-label={"Full name"}
                    aria-describedby={`company_name-error`}
                    aria-required={true}
                    onChange={handleChange}
                    className="input text-subtitle-3 font-bold mb-[10px]"
                  />
                  <label
                    htmlFor="company_name"
                    className="text-subtitle-5 font-regular"
                  >
                    Company
                  </label>
                  <ErrorMessage
                    name="company_name"
                    component="span"
                    className="text-red-500"
                  />
                </div>
                <div className="input-group relative w-full">
                  <Field
                    name="job_title"
                    placeholder=""
                    value={values.job_title}
                    aria-label="job_title"
                    aria-required={true}
                    onChange={handleChange}
                    aria-describedby={`job_title-error`}
                    className="input text-subtitle-3 font-bold mb-[10px]"
                  />
                  <label
                    htmlFor="job_title"
                    className="text-subtitle-5 font-regular"
                  >
                    Job Title
                  </label>
                  <ErrorMessage
                    name="job_title"
                    component="span"
                    className="text-red-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center md:justify-between justify-end md:flex-nowrap flex-wrap gap-6 mt-6">
              <div className="flex items-center gap-8 md:w-auto w-full">
                <div className="flex items-center gap-3">
                  <Image
                    src="/images/confidential.svg"
                    alt="confidential"
                    width={18}
                    height={18}
                  />
                  <span className="text-primary-text text-subtitle-4 font-regular">
                    100% confidential
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Image
                    src="/images/confidential.svg"
                    alt="confidential"
                    width={18}
                    height={18}
                  />
                  <span className="text-primary-text text-subtitle-4 font-regular">
                    We sign NDA
                  </span>
                </div>
              </div>
              <Button
                variant="small"
                className="my-0"
                label={data?.button_text || "Submit"}
                type="submit"
              />
            </div>
            {sitekey && (
              <ReCAPTCHA
                style={{ display: "inline" }}
                theme="dark"
                size="invisible"
                ref={reCaptchaRef}
                sitekey={sitekey}
              />
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};
export default DownloadWhitepaper;
